import type {CSSProperties} from 'react';
import {font, radius, space, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeTeachLook = 'teach-paste-toggle' | 'teach-example' | 'teach-pill' | 'teach-pill-quiet' | 'teach-save-sm' | 'teach-cancel-sm' | 'teach-delete-sm' | 'teach-icon-add' | 'teach-icon-row';
// The textareas: the paste block, and the body of a row in edit mode and in the add form.
export type WedgeTeachFieldVariant = 'wedge-teach-paste-area' | 'wedge-teach-row-area' | 'wedge-teach-row-area-add';
// The titles of a row in edit mode and in the add form: one bare native input each.
export type WedgeTeachTitleVariant = 'wedge-teach-row-title' | 'wedge-teach-row-title-add';

const TEACH_LOOKS = new Set<string>(['teach-paste-toggle', 'teach-example', 'teach-pill', 'teach-pill-quiet', 'teach-save-sm', 'teach-cancel-sm', 'teach-delete-sm', 'teach-icon-add', 'teach-icon-row']);
export const isWedgeTeachLook = (look: string): look is WedgeTeachLook => TEACH_LOOKS.has(look);
const TEACH_AREAS = new Set<unknown>(['wedge-teach-paste-area', 'wedge-teach-row-area', 'wedge-teach-row-area-add']);
export const isWedgeTeachFieldVariant = (variant: unknown): variant is WedgeTeachFieldVariant => TEACH_AREAS.has(variant);
export const isWedgeTeachTitleVariant = (variant: unknown): variant is WedgeTeachTitleVariant => variant === 'wedge-teach-row-title' || variant === 'wedge-teach-row-title-add';
/* No Teach look draws a transparent target (wedgeSettingsDrawsHit). The two icon marks are boxes of 44px each way, so a region could only repeat
   the box. For the rest, a browser check of the whole Teach list at device pixel ratio 1 (light and sun, 390 and 1440 wide, a same-side noise
   control of 0) found the anchors changed 7 to 11 pixels at the corners of the textarea under the toggle with every anchor on against off; the
   toggle, the pills and the example card each remove it alone. At ratio 2 the small pills moved about 180 pixels of the list. */
const NO_HIT = new Set<string>(['teach-paste-toggle', 'teach-example', 'teach-pill', 'teach-pill-quiet', 'teach-save-sm', 'teach-cancel-sm', 'teach-delete-sm', 'teach-icon-add', 'teach-icon-row']);
export const isWedgeTeachNoHitLook = (look: string): boolean => NO_HIT.has(look);

/* Released Teach guide (Settings, Blip sheet), the paste block and the empty state. Every box is a shipped inline style, off-scale numbers
   included; each color reads the exact legacy role, so the shipped ink rules keep matching, and a legacy button sets no font family, so none does.
   Paste toggle is a full-width dashed block (1px dashed card border, 13px radius, 11px by 13px padding, 13px semibold secondary type, 12px
   margin below) on the surface fill. An example card is the left-aligned block of the empty state (0.5px card border, 13px radius, 11px by
   13px padding, 13px type, primary ink) on the surface fill; the caller prints its two lines. Teach pill is the Add and Save pill (8px by 16px
   padding, 10px radius, no border, accent fill, on-accent ink, 13px bold) and Teach pill quiet the Cancel pill (0.5px separator border, no
   fill, primary ink). Neither pill dims when it is disabled, as the source has no disabled style. The paste area is one bare textarea
   (10px padding, 10px radius, 1px card border, elevated fill, 13px type, vertical resize). The caller owns the value, the rows, the length
   cap, the handlers and the glyph.
   The rows of a group: the small pills (Save, Add, Cancel and Delete) take 6px by 12px padding and 12px type on the same bold weight, fill
   and border as the two pills; Delete takes the red fill. The row title is a bare input (7px by 9px padding, 8px radius, 1px card border,
   13px bold type, 6px margin below) and the row body a bare textarea (12.5px type); each has an elevated fill in edit mode and the surface
   fill in the add form. The two icon marks are 44px boxes with a negative margin that keeps the layout of the glyph (add 4px padding and
   accent ink; edit and delete 3px padding and secondary ink). */
const BLOCK_RADIUS = 13, BLOCK_PAD = '11px 13px', PILL_PAD = `${space[2]}px ${space[4]}px`, PASTE_PAD = 10, PASTE_MARGIN = 12;
const SMALL_PAD = '6px 12px', ROW_PAD = '7px 9px', ROW_AREA_FONT = 12.5, ICON_BOX = 44;

export function wedgeTeachStyle(look: WedgeTeachLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'teach-paste-toggle': return {
      width: '100%', textAlign: 'left', padding: BLOCK_PAD, borderRadius: BLOCK_RADIUS, border: `1px dashed ${r('cardBorder')}`, background: r('surface'),
      color: r('labelSec'), fontSize: font.footnote, fontWeight: weight.semibold, cursor: 'pointer', marginBottom: PASTE_MARGIN,
    };
    case 'teach-example': return {
      textAlign: 'left', padding: BLOCK_PAD, borderRadius: BLOCK_RADIUS, border: `0.5px solid ${r('cardBorder')}`, background: r('surface'),
      color: r('label'), fontSize: font.footnote, cursor: 'pointer',
    };
    case 'teach-pill': return {
      padding: PILL_PAD, borderRadius: radius.md, border: 'none', background: r('accent'), color: r('onAccent'), fontSize: font.footnote,
      fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'teach-pill-quiet': return {
      padding: PILL_PAD, borderRadius: radius.md, border: `0.5px solid ${r('separator')}`, background: 'transparent', color: r('label'),
      fontSize: font.footnote, fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'teach-save-sm': return {
      padding: SMALL_PAD, borderRadius: radius.md, border: 'none', background: r('accent'), color: r('onAccent'), fontSize: font.caption,
      fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'teach-cancel-sm': return {
      padding: SMALL_PAD, borderRadius: radius.md, border: `0.5px solid ${r('separator')}`, background: 'transparent', color: r('label'),
      fontSize: font.caption, fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'teach-delete-sm': return {
      padding: SMALL_PAD, borderRadius: radius.md, border: 'none', background: r('red'), color: r('onAccent'), fontSize: font.caption,
      fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'teach-icon-add': return {
      border: 'none', background: 'transparent', color: r('accent'), cursor: 'pointer', padding: 4, minWidth: ICON_BOX, minHeight: ICON_BOX,
      margin: '-11.5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
    case 'teach-icon-row': return {
      border: 'none', background: 'transparent', color: r('labelSec'), cursor: 'pointer', padding: 3, minWidth: ICON_BOX, minHeight: ICON_BOX,
      margin: '-12.5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
  }
}

export function wedgeTeachFieldStyle(variant: WedgeTeachFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  if (variant === 'wedge-teach-paste-area') return {
    width: '100%', boxSizing: 'border-box', padding: PASTE_PAD, borderRadius: radius.md, border: `1px solid ${r('cardBorder')}`, background: r('bgElev'),
    color: r('label'), fontSize: font.footnote, fontFamily: r('font'), resize: 'vertical',
  };
  return {
    width: '100%', boxSizing: 'border-box', padding: ROW_PAD, borderRadius: radius.sm, border: `1px solid ${r('cardBorder')}`,
    background: variant === 'wedge-teach-row-area' ? r('bgElev') : r('surface'), color: r('label'), fontSize: ROW_AREA_FONT, fontFamily: r('font'), resize: 'vertical',
  };
}

export function wedgeTeachTitleStyle(variant: WedgeTeachTitleVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', boxSizing: 'border-box', padding: ROW_PAD, borderRadius: radius.sm, border: `1px solid ${r('cardBorder')}`,
    background: variant === 'wedge-teach-row-title' ? r('bgElev') : r('surface'), color: r('label'), fontSize: font.footnote, fontWeight: weight.bold,
    marginBottom: 6, fontFamily: r('font'),
  };
}
